import React, { useEffect, useMemo, useState, useRef } from 'react';
import { UserProfile, GeneratedImage, CreditTransaction } from '../types';
import { 
  LogOut, CreditCard, Sparkles, X, Mail, Loader2, 
  Globe, Package, Zap, Check, AlertCircle, 
  Image as ImageIcon, Settings, Clock, Download, 
  LayoutDashboard, CloudUpload, History, Receipt,
  Eye, ShoppingBag, CheckCircle2, RefreshCw, ShieldAlert, Trash2, Edit3,
  Maximize2, ChevronLeft, ChevronRight, Archive, Sofa, MoonStar, Link2, Heart, Upload
} from 'lucide-react';
import {
  ReHubActivityHistory,
  ReHubBatchActions,
  ReHubDashboardShell,
  ReHubEmptyState,
  ReHubFilterBar,
  ReHubGallery,
  ReHubImageCard,
  ReHubImageViewer,
  ReHubLoadingSkeleton,
  ReHubStat,
  ReHubUploadPanel,
  applyGalleryQuery,
  pageSlice,
  type ReHubAppConfig,
  type ReHubNotice,
  type ReHubUploadFile,
} from '@rehub/dashboard';
import { formatPhotoclearCreditLabel, quotePhotoclearOperation } from '@rehub/pricing';
import { DECLUTTER_INTENSITIES } from '../constants';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE_MB } from '../constants';
import { getUserImages, getUserTransactions, supabase, isConfigured, redeemPromoCode, getThumbnailUrl as getSupabaseThumbnailUrl } from '../services/supabase';
import { AIContentDisclosure, AIInfoPanel, APP_DEFAULT_CLASSIFICATION, APP_DEFAULT_OPERATION, type AIContentClassification } from '@rehub/ai-transparency';
import JSZip from 'jszip';

import { buildDashboardProductCatalog } from '@rehub/pricing';

// Shared catalogue — amounts and Stripe IDs come from @rehub/pricing.
const PRODUCT_CATALOG = buildDashboardProductCatalog(
  ((import.meta as any).env || {}) as Record<string, string | undefined>
);
// ── Buy only what you need: per-credit price (quantity in checkout) ──
const MIN_CUSTOM_CREDITS = 5;
const MAX_CUSTOM_CREDITS = 500;
const EXACT_CREDITS = {
  name: 'Buy exact credits',
  description: 'Minimum of 5 credits. Pay only for the credits you need — no pack discount.',
  prices: {
    USD: { id: (import.meta as any).env?.VITE_STRIPE_PRICE_PER_CREDIT_USD || 'price_1T1NYIDHlg75FZHH46JYdTMI', amountPerCredit: 1.05 },
    GBP: { id: (import.meta as any).env?.VITE_STRIPE_PRICE_PER_CREDIT_GBP || 'price_1T1NXsDHlg75FZHHmn69RRi7', amountPerCredit: 0.75 },
  },
};

interface DashboardProps {
  user: UserProfile;
  onClose: () => void;
  onLogout: () => void;
  onOpenEditor: (file: File, intensityId?: string) => void;
  onConsumeCredit: () => Promise<'success' | 'insufficient_credits' | 'error'>;
  onContinueEditing: (file: File) => void;
  currentApp?: 'photoclear' | 'staging'; // Which app is hosting this dashboard
}

export default function Dashboard({ user, onClose, onLogout, onOpenEditor, onConsumeCredit, onContinueEditing, currentApp = 'photoclear' }: DashboardProps) {
  const [activeSection, setActiveSection] = useState<'photos' | 'credits' | 'transactions' | 'settings'>('photos');
  
  // App-specific branding
  const isPhotoclear = currentApp === 'photoclear';
  
  const logoPath = isPhotoclear
    ? '/Photoclear Logo HR Trans.png'
    : '/Virtual Staging Studio Logo (500 × 300 Px) - 2.png';
  
  // --- CURRENCY DETECTION LOGIC ---
  const [currency, setCurrency] = useState<string>('USD');

  useEffect(() => {
    // Run once on mount to determine currency
    try {
      const locale = navigator.language;
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (locale === 'en-GB' || timeZone === 'Europe/London') {
        setCurrency('GBP');
      } else {
        setCurrency('USD');
      }
    } catch (e) {
      setCurrency('USD');
    }
  }, []);
  
  const [processingCheckout, setProcessingCheckout] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [billingTab, setBillingTab] = useState<'subscription' | 'topup'>('subscription');
  const [customCreditsAmount, setCustomCreditsAmount] = useState(25);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [userImages, setUserImages] = useState<GeneratedImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionFilter, setTransactionFilter] = useState<'all' | 'purchase' | 'usage' | 'refund' | 'bonus'>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<GeneratedImage | null>(null);
  const [lightboxShowOriginal, setLightboxShowOriginal] = useState(false);
  const [lightboxImageLoaded, setLightboxImageLoaded] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [galleryPage, setGalleryPage] = useState(0);
const [bulkDownloading, setBulkDownloading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  
  // Service type filter for gallery - default to current app
  const [serviceFilter, setServiceFilter] = useState<'all' | 'photoclear' | 'staging' | 'favourites' | 'processing' | 'failed'>(currentApp === 'staging' ? 'staging' : 'photoclear');
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [dateRange, setDateRange] = useState<'all' | '7d' | '30d' | '90d'>('all');
  const [galleryView, setGalleryView] = useState<'grid' | 'list'>('grid');
  const [favourites, setFavourites] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<ReHubUploadFile[]>([]);
  const [intensityId, setIntensityId] = useState<'light' | 'medium' | 'full'>('medium');
  const [notices, setNotices] = useState<ReHubNotice[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [redeemingPromo, setRedeemingPromo] = useState(false);
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSendToVirtualStaging = async (imageUrl: string) => {
    const base = 'https://staging.rehub.studio';
    const search = `?imageUrl=${encodeURIComponent(imageUrl)}`;
    let hash = '';
    if (isConfigured) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token && session?.refresh_token) {
          hash = `#sso_access_token=${encodeURIComponent(session.access_token)}&sso_refresh_token=${encodeURIComponent(session.refresh_token)}`;
        }
      } catch (_) {
        // open without SSO if session read fails
      }
    }
    window.open(`${base}${search}${hash}`, '_blank', 'noopener,noreferrer');
  };

  const handleSendToHyperdusk = async (imageUrl: string) => {
    const base = 'https://hyperdusk.rehub.studio';
    const search = `?imageUrl=${encodeURIComponent(imageUrl)}`;
    let hash = '';
    if (isConfigured) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token && session?.refresh_token) {
          hash = `#sso_access_token=${encodeURIComponent(session.access_token)}&sso_refresh_token=${encodeURIComponent(session.refresh_token)}`;
        }
      } catch (_) {}
    }
    window.open(`${base}${search}${hash}`, '_blank', 'noopener,noreferrer');
  };
  
const IMAGES_PER_PAGE = 12;

  useEffect(() => {
    if (!user?.id) return;
    try {
      const stored = localStorage.getItem(`rehub-favourites:${user.id}`);
      const ids = stored ? JSON.parse(stored) : [];
      setFavourites(new Set(Array.isArray(ids) ? ids : []));
    } catch {
      setFavourites(new Set());
    }
  }, [user?.id]);

  const toggleFavourite = (id: string) => {
    setFavourites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (user?.id) localStorage.setItem(`rehub-favourites:${user.id}`, JSON.stringify([...next]));
      return next;
    });
  };

  const imageTitle = (img: GeneratedImage) => {
    if (img.type && img.type.length <= 42 && !img.type.includes(' ')) return img.type;
    return img.service_type === 'staging' ? 'Staged photo' : 'PhotoClear photo';
  };

  const galleryRecords = useMemo(() => userImages.map((img) => ({
    img,
    id: img.id,
    name: imageTitle(img),
    status: img.status,
    service: img.service_type,
    createdAt: img.created_at,
    favourite: favourites.has(img.id),
    searchText: `${img.prompt || ''} ${img.type || ''} ${img.id}`,
  })), [userImages, favourites]);

  const filteredRecords = useMemo(() => applyGalleryQuery(galleryRecords, {
    search,
    service: serviceFilter === 'photoclear' || serviceFilter === 'staging' ? serviceFilter : null,
    status: serviceFilter === 'processing' || serviceFilter === 'failed' ? serviceFilter : null,
    favouritesOnly: serviceFilter === 'favourites',
    sort: sortOrder,
    range: dateRange,
  }), [galleryRecords, search, serviceFilter, sortOrder, dateRange]);

  const filteredImages = filteredRecords.map((record) => record.img);
  const displayedImages = pageSlice(filteredImages, galleryPage, IMAGES_PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(filteredImages.length / IMAGES_PER_PAGE));
  const viewableImages = filteredImages.filter((img) => img.output_url || img.input_url);

  // Handle opening Stripe Customer Portal for subscription management
  const handleManageSubscription = async () => {
    if (!user.stripe_customer_id) {
      alert('No subscription found. Please subscribe first.');
      return;
    }
    
    setLoadingPortal(true);
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: {
          customerId: user.stripe_customer_id,
          returnUrl: `${window.location.origin}/dashboard`
        }
      });
      
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      console.error('Portal error:', err);
      alert('Failed to open subscription management. Please try again.');
    } finally {
      setLoadingPortal(false);
    }
  };

  // Check for Payment Success Query Param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
        setPaymentSuccess(true);
        setNotices((prev) => [{ id: 'payment-success', title: 'Payment successful', body: 'Credits have been added to your account.', tone: 'success' }, ...prev.filter((notice) => notice.id !== 'payment-success')]);
        window.history.replaceState({}, '', window.location.pathname);
        // Refresh transactions after a delay to allow webhook to process
        setTimeout(() => {
             if (user.id) {
                 getUserTransactions(user.id).then(setTransactions);
             }
        }, 2000);
        
        // Auto-dismiss success message
        setTimeout(() => setPaymentSuccess(false), 8000);
    }
  }, []);

  // Auto-cleanup stuck processing images (processing for > 5 minutes)
  const cleanupStuckImages = async (images: GeneratedImage[]) => {
    const STUCK_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
    const now = Date.now();
    
    const stuckImages = images.filter(img => {
      if (img.status !== 'processing') return false;
      const createdAt = new Date(img.created_at).getTime();
      return (now - createdAt) > STUCK_THRESHOLD_MS;
    });
    
    if (stuckImages.length > 0) {
      console.log(`Found ${stuckImages.length} stuck processing image(s), marking as failed`);
      
      for (const img of stuckImages) {
        await supabase.from('images').update({ status: 'failed' }).eq('id', img.id);
      }
      
      // Update local state
      setUserImages(prev => prev.map(img => 
        stuckImages.some(s => s.id === img.id) ? { ...img, status: 'failed' } : img
      ));
    }
  };

  useEffect(() => {
    if (user?.id) {
        setLoadingImages(true);
        getUserImages(user.id)
            .then(imgs => {
              setUserImages(imgs);
              // Check for stuck images after loading
              cleanupStuckImages(imgs);
            })
            .catch(err => console.error(err))
            .finally(() => setLoadingImages(false));

        const channel = supabase.channel('dashboard-gallery')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'images', filter: `user_id=eq.${user.id}` }, 
            (payload) => {
                if (payload.eventType === 'INSERT') {
                    setUserImages(prev => [payload.new as GeneratedImage, ...prev]);
                } else if (payload.eventType === 'UPDATE') {
                    setUserImages(prev => prev.map(img => img.id === payload.new.id ? payload.new as GeneratedImage : img));
                }
            })
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }
  }, [user?.id]);

  useEffect(() => {
    if (activeSection === 'transactions' && user?.id) {
        setLoadingTransactions(true);
        getUserTransactions(user.id)
            .then(data => setTransactions(data))
            .finally(() => setLoadingTransactions(false));
    }
  }, [activeSection, user?.id]);

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (ACCEPTED_IMAGE_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE_MB * 1024 * 1024) {
        onOpenEditor(file);
      }
    }
  };

  // Generate thumbnail URL with Supabase image transforms
  const getThumbnailUrl = (url: string, width: number = 300) => {
    if (!url) return url;
    const thumbnailUrl = getSupabaseThumbnailUrl(url, {
      width,
      height: Math.round(width * 0.75),
      quality: 70,
      resize: 'cover',
    });
    return thumbnailUrl || url;
  };

  // Generate optimized lightbox URL (larger but still optimized)
  const getLightboxUrl = (url: string) => {
    if (!url) return url;
    const optimizedUrl = getSupabaseThumbnailUrl(url, {
      width: 1600,
      height: 1200,
      quality: 85,
      resize: 'contain',
    });
    return optimizedUrl || url;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
        const file = e.target.files[0];
        onOpenEditor(file);
    }
    e.target.value = '';
  };

  const handleRedeemPromo = async () => {
    if (!promoCode.trim()) {
      setPromoMessage({ type: 'error', text: 'Please enter a promo code' });
      return;
    }

    setRedeemingPromo(true);
    setPromoMessage(null);

    try {
      const result = await redeemPromoCode(promoCode.trim());

      if (result.success) {
        setPromoMessage({ 
          type: 'success', 
          text: `Success! ${result.credits_added} credits added to your account.` 
        });
        setPromoCode('');
        
        // Refresh user data to show updated credits
        if (user.id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('credits')
            .eq('id', user.id)
            .single();
          
          if (profile) {
            user.credits = profile.credits;
          }
          
          // Refresh transactions
          getUserTransactions(user.id).then(setTransactions);
        }
      } else {
        const errorMessages: Record<string, string> = {
          'NOT_AUTHENTICATED': 'You must be logged in to redeem a code',
          'INVALID_CODE': 'Invalid promo code',
          'CODE_INACTIVE': 'This promo code is no longer active',
          'CODE_EXPIRED': 'This promo code has expired',
          'CODE_EXHAUSTED': 'This promo code has reached its usage limit',
          'ALREADY_REDEEMED': 'You have already redeemed this code'
        };
        
        setPromoMessage({ 
          type: 'error', 
          text: errorMessages[result.error || ''] || 'Failed to redeem code' 
        });
      }
    } catch (err) {
      console.error('Redeem promo error:', err);
      setPromoMessage({ type: 'error', text: 'An error occurred. Please try again.' });
    } finally {
      setRedeemingPromo(false);
    }
  };

  const handleCheckout = async (credits: number, priceId: string, mode: 'subscription' | 'payment', quantity?: number) => {
    setCheckoutError(null);
    const qty = quantity ?? 1;
    console.log("Initiating checkout:", { credits, priceId, mode, quantity: qty, userId: user?.id });

    if (!isConfigured) {
        setCheckoutError("System Error: Application is not connected to Supabase backend.");
        return;
    }

    if (!user || !user.id) {
        setCheckoutError("System Error: User ID not found.");
        return;
    }
    if (!priceId) {
        setCheckoutError("Product Configuration Error: Missing Price ID.");
        return;
    }
    
    const loadingId = qty > 1 ? `${priceId}_${qty}` : priceId;
    setProcessingCheckout(loadingId);
    
    try {
      const metadata = { 
          credit_amount: String(credits),
          credits: String(credits),
          user_id: user.id 
      };

      const checkoutBody = {
          priceId: priceId,
          mode: mode, 
          quantity: qty,
          client_reference_id: user.id, 
          userId: user.id,
          successUrl: `https://hub.rehub.studio/#/purchase-complete?app=photoclear&type=credits&return=${encodeURIComponent(window.location.origin + '/dashboard')}&session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${window.location.origin}/dashboard?canceled=true`,
          metadata: metadata
      };

      // Renamed to 'api-payment' to avoid AdBlockers which block urls containing 'checkout'
      // Add a timeout so checkout never hangs indefinitely on blocked/stalled requests.
      const invokePromise = supabase.functions.invoke('api-payment', {
        body: checkoutBody,
        headers: { 'Content-Type': 'application/json' }
      });
      const timeoutPromise = new Promise<{ data: null; error: { message: string } }>((resolve) => {
        setTimeout(() => {
          resolve({
            data: null,
            error: {
              message: "Request timed out. Your network, browser extension, or firewall may be blocking checkout.",
            },
          });
        }, 15000);
      });
      const { data, error } = (await Promise.race([invokePromise, timeoutPromise])) as Awaited<typeof invokePromise>;

      if (error) {
          console.error("Function Invoke Error Full:", error);
          
          let msg = "Payment Service Error.";
          // Handle specific Supabase "Failed to send request" error which indicates network failure
          // Also explicitly handle 500s which might be adblockers returning empty responses
          if (error.message && (error.message.includes("Failed to send a request") || error.message.includes("fetch failed"))) {
              msg = "Connection Blocked: Your browser or an extension (like AdBlock/uBlock) blocked the payment request. Please disable ad-blockers for this site and try again.";
          } else if (typeof error === 'object' && error !== null) {
              if (error.context && error.context.status === 500) {
                   msg = "Server Configuration Error. (Hint: Missing STRIPE_SECRET_KEY in Edge Function Secrets?)";
              } else {
                   msg = error.message || JSON.stringify(error);
              }
          }
          throw new Error(msg);
      }

      if (data?.error) {
          throw new Error(data.error);
      }
      
      if (data?.url) {
          window.location.href = data.url;
      } else {
          throw new Error("No payment URL received from server.");
      }

    } catch (err: any) {
      console.error("Checkout Exception:", err);
      setCheckoutError(err.message || 'Checkout failed due to an unexpected error.');
    } finally {
      setProcessingCheckout(null);
    }
  };

  const handleDeleteAccount = async () => {
    if (!isConfigured) {
      alert('System not configured');
      return;
    }

    setDeletingAccount(true);
    try {
      const { data, error } = await supabase.functions.invoke('delete-account', {
        body: {},
      });

      if (error) {
        throw new Error(error.message || 'Failed to delete account');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Sign out and redirect
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (err: any) {
      console.error('Delete account error:', err);
      alert(err.message || 'Failed to delete account. Please try again.');
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  const getDaysRemaining = (dateStr: string) => {
    const date = new Date(dateStr);
    const expires = new Date(date.getTime() + 7 * 24 * 60 * 60 * 1000); 
    const now = new Date();
    const diff = expires.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 3600 * 24)));
  };

  const handleDelete = async (id: string) => {
      if (!confirm('Are you sure you want to delete this image?')) return;
      
      setDeletingId(id);
      try {
        const { error } = await supabase
          .from('images')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        
        setUserImages(prev => prev.filter(img => img.id !== id));
      } catch (err) {
        console.error('Delete failed:', err);
        alert('Failed to delete image. Please try again.');
      } finally {
        setDeletingId(null);
      }
  };

  const handleCopyLink = async (imageUrl: string, id: string) => {
      if (!imageUrl) return;
      try {
        await navigator.clipboard.writeText(imageUrl);
      } catch {
        // Fallback for browsers/contexts without the async clipboard API
        const ta = document.createElement('textarea');
        ta.value = imageUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); } catch (_) {}
        document.body.removeChild(ta);
      }
      setCopiedId(id);
      setTimeout(() => setCopiedId(prev => (prev === id ? null : prev)), 2000);
  };

  const handleDownload = async (imageUrl: string, id: string) => {
      setDownloadingId(id);
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        let ext = 'jpg';
        if (blob.type === 'image/png') ext = 'png';
        else if (blob.type === 'image/webp') ext = 'webp';
        link.download = `${isPhotoclear ? 'Photoclear' : 'VirtualStaging'}-${Date.now()}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Download failed:", err);
        const link = document.createElement('a');
        link.href = imageUrl;
        link.target = '_blank';
        link.download = `${isPhotoclear ? 'Photoclear' : 'VirtualStaging'}-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        setDownloadingId(null);
      }
  };

  const handleContinueEdit = async (imageUrl: string, id: string) => {
      setEditingId(id);
      try {
        // Fetch the image from the URL
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        
        // Convert blob to File object
        const fileName = `staging-edit-${Date.now()}.jpg`;
        const file = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
        
        // Close dashboard and load image into editor without charging
        onContinueEditing(file);
      } catch (err) {
        console.error("Failed to load image for editing:", err);
        alert('Failed to load image. Please try again.');
      } finally {
        setEditingId(null);
      }
  };

const handleBulkDownload = async () => {
      // Use filtered images (respects current service filter)
      const completedImages = filteredImages.filter(img => img.status === 'completed' && img.output_url);
      
      if (completedImages.length === 0) {
        alert('No completed images to download.');
        return;
      }
      
      const filterLabel = serviceFilter === 'all' ? '' : ` ${serviceFilter}`;
      if (!confirm(`Download all ${completedImages.length}${filterLabel} completed images as a ZIP file? This may take a moment.`)) {
        return;
      }
      
      setBulkDownloading(true);
      setBulkProgress(0);
      
      try {
        const zip = new JSZip();
        
        // Use appropriate prefix based on filter
        const prefix = serviceFilter === 'staging' ? 'VirtualStaging' : isPhotoclear ? 'Photoclear' : 'RehubStudio';
        
        for (let i = 0; i < completedImages.length; i++) {
          const img = completedImages[i];
          try {
            const response = await fetch(img.output_url);
            const blob = await response.blob();
            
            // Determine file extension
            let ext = 'jpg';
            if (blob.type === 'image/png') ext = 'png';
            else if (blob.type === 'image/webp') ext = 'webp';
            
            // Add to ZIP with sequential numbering
            zip.file(`${prefix}-${String(i + 1).padStart(4, '0')}.${ext}`, blob);
          } catch (err) {
            console.error(`Failed to fetch image ${i + 1}:`, err);
          }
          
          setBulkProgress(Math.round(((i + 1) / completedImages.length) * 90));
        }
        
        // Generate ZIP file
        setBulkProgress(95);
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        
        // Download ZIP with appropriate filename
        const url = window.URL.createObjectURL(zipBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${prefix}-Images-${new Date().toISOString().split('T')[0]}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        setBulkProgress(100);
      } catch (err) {
        console.error('Bulk download failed:', err);
        alert('Failed to create ZIP file. Please try again.');
      } finally {
        setBulkDownloading(false);
        setBulkProgress(0);
      }
  };

  // --- GET DISPLAY PRODUCTS ---
  const getDisplayProducts = (type: 'subscription' | 'topup') => {
    try {
        const list = type === 'subscription' ? PRODUCT_CATALOG.SUBSCRIPTIONS : PRODUCT_CATALOG.TOPUPS;
        if (!list || list.length === 0) return [];
        
        return list.map(product => {
            const priceMap = product.prices as any;
            const priceData = priceMap[currency] || priceMap['USD'] || Object.values(priceMap)[0];
            
            if (!priceData) return null;

            // Handle costPerCredit - can be string (subscriptions) or object (topups)
            let costPerCreditText = null;
            const costPerCreditData = (product as any).costPerCredit;
            if (costPerCreditData) {
                if (typeof costPerCreditData === 'string') {
                    costPerCreditText = costPerCreditData;
                } else if (typeof costPerCreditData === 'object') {
                    costPerCreditText = costPerCreditData[currency] || costPerCreditData['USD'];
                }
            }

                return {
                id: product.id,
                name: product.name,
                credits: product.credits,
                bonus: product.bonus,
                popular: product.popular,
                displayAmount: (priceData as any).amount.toFixed(2),
                priceId: (priceData as any).id,
                currency: currency,
                costPerCredit: costPerCreditText,
                valueLabel: (product as any).valueLabel || null,
                thirdFeature: (product as any).thirdFeature || null
            };
        }).filter(p => p !== null);
    } catch (e) {
        console.error("Error generating products", e);
        return [];
    }
  };

  const displayProducts = getDisplayProducts(billingTab);

  const addUploadFiles = (incoming: File[]) => {
    setUploadFiles((prev) => [
      ...prev,
      ...incoming.map((file) => {
        let error: string | undefined;
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) error = 'Use a JPEG, PNG or WebP photo.';
        else if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) error = `Maximum size is ${MAX_FILE_SIZE_MB} MB.`;
        return { id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`, file, error };
      }),
    ]);
  };

  const startUpload = () => {
    const ready = uploadFiles.find((item) => !item.error);
    if (!ready) return;
    onOpenEditor(ready.file, intensityId);
  };

  const openApp = async (app: ReHubAppConfig, event: React.MouseEvent<HTMLAnchorElement>) => {
    if (app.appId === currentApp) {
      event.preventDefault();
      return;
    }
    if (!isConfigured) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token && session?.refresh_token) {
        event.preventDefault();
        const base = app.appId === 'assistant' ? `${app.url}?sourceApp=${currentApp}` : app.url;
        window.location.href = `${base}#sso_access_token=${encodeURIComponent(session.access_token)}&sso_refresh_token=${encodeURIComponent(session.refresh_token)}`;
      }
    } catch {
      // The link still navigates without a session handoff.
    }
  };

  const deleteImages = async (ids: string[]) => {
    if (ids.length === 0) return;
    const label = ids.length === 1 ? 'this photo' : `${ids.length} photos`;
    if (!confirm(`Delete ${label}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    const failed: string[] = [];
    for (const id of ids) {
      const { error } = await supabase.from('images').delete().eq('id', id);
      if (error) failed.push(id);
    }
    setUserImages((prev) => prev.filter((img) => !ids.includes(img.id) || failed.includes(img.id)));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => { if (!failed.includes(id)) next.delete(id); });
      return next;
    });
    if (failed.length > 0) {
      alert(`Deleted ${ids.length - failed.length}. ${failed.length} could not be deleted.`);
    }
    setBulkDeleting(false);
  };

  const downloadSelected = async () => {
    const chosen = filteredImages.filter((img) => selectedIds.has(img.id) && img.status === 'completed' && img.output_url);
    if (chosen.length === 0) {
      alert('Select completed photos to download.');
      return;
    }
    if (!confirm(`Download ${chosen.length} selected photo${chosen.length === 1 ? '' : 's'} as a ZIP?`)) return;
    setBulkDownloading(true);
    setBulkProgress(0);
    try {
      const zip = new JSZip();
      let saved = 0;
      for (let i = 0; i < chosen.length; i++) {
        try {
          const response = await fetch(chosen[i].output_url);
          const blob = await response.blob();
          let ext = 'jpg';
          if (blob.type === 'image/png') ext = 'png';
          else if (blob.type === 'image/webp') ext = 'webp';
          zip.file(`Photoclear-${String(saved + 1).padStart(4, '0')}.${ext}`, blob);
          saved += 1;
        } catch (err) {
          console.error('Selected download failed', err);
        }
        setBulkProgress(Math.round(((i + 1) / chosen.length) * 90));
      }
      if (saved === 0) throw new Error('empty');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Photoclear-Selected-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      if (saved < chosen.length) alert(`Downloaded ${saved} of ${chosen.length}. Some photos could not be fetched.`);
    } catch (err) {
      console.error(err);
      alert('Failed to create the ZIP file. Please try again.');
    } finally {
      setBulkDownloading(false);
      setBulkProgress(0);
    }
  };

  const sectionTitle = activeSection === 'photos'
    ? (serviceFilter === 'favourites' ? 'Favourites' : 'My Photos')
    : activeSection === 'credits' ? 'Buy Credits'
    : activeSection === 'transactions' ? 'History'
    : 'Settings';

  const enhancedCount = userImages.filter((img) => img.status === 'completed' && img.output_url).length;
  const lightboxIndex = lightboxImage ? viewableImages.findIndex((img) => img.id === lightboxImage.id) : -1;
  const showOriginalMissing = Boolean(lightboxImage && !lightboxImage.input_url);
  const viewerImage = lightboxImage
    ? getLightboxUrl(lightboxShowOriginal && lightboxImage.input_url ? lightboxImage.input_url : (lightboxImage.output_url || lightboxImage.input_url))
    : null;

  const formatCardDate = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <>
    <ReHubDashboardShell
      appId={currentApp}
      logoSrc={logoPath}
      accentColour={isPhotoclear ? '#f97316' : '#a855f7'}
      nav={[
        { id: 'photos', label: 'My Photos', icon: ImageIcon },
        { id: 'credits', label: 'Buy Credits', icon: CreditCard },
        { id: 'transactions', label: 'History', icon: Receipt },
        { id: 'favourites', label: 'Favourites', icon: Heart },
        { id: 'settings', label: 'Settings', icon: Settings },
      ]}
      activeNavId={activeSection === 'photos' && serviceFilter === 'favourites' ? 'favourites' : activeSection}
      onNavigate={(id) => {
        if (id === 'favourites') {
          setActiveSection('photos');
          setServiceFilter('favourites');
        } else {
          setActiveSection(id as 'photos' | 'credits' | 'transactions' | 'settings');
          if (id === 'photos') setServiceFilter('all');
        }
        setGalleryPage(0);
      }}
      user={{ name: user.email.split('@')[0], email: user.email, plan: user.subscription_plan }}
      credits={user.credits}
      onAddCredits={() => setActiveSection('credits')}
      onLogout={onLogout}
      onClose={onClose}
      showUpgrade={!user.subscription_plan}
      onUpgrade={() => setActiveSection('credits')}
      search={activeSection === 'photos' ? search : ''}
      onSearchChange={activeSection === 'photos' ? (value) => { setSearch(value); setGalleryPage(0); } : undefined}
      searchPlaceholder="Search your photos…"
      notices={notices}
      onDismissNotice={(id) => setNotices((prev) => prev.filter((notice) => notice.id !== id))}
      onOpenApp={openApp}
      pageTitle={sectionTitle}
      pageSubtitle={activeSection === 'photos' ? 'Manage, edit and enhance your property images with AI.' : undefined}
      headerActions={activeSection === 'photos' ? (
        <>
          <button type="button" onClick={() => setUploadOpen(true)} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-[#101622] px-3 py-2 text-sm font-medium text-slate-100 hover:bg-white/5">
            <Upload size={16} /> Upload Photos
          </button>
          <button type="button" onClick={() => setUploadOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-400">
            <Sparkles size={16} /> New Restoration
          </button>
        </>
      ) : undefined}
    >
      {paymentSuccess && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/15 px-4 py-3 text-sm text-emerald-100">
          <span className="inline-flex items-center gap-2"><CheckCircle2 size={18} /> Payment successful. Credits have been added to your account.</span>
          <button type="button" onClick={() => setPaymentSuccess(false)} aria-label="Dismiss payment message"><X size={16} /></button>
        </div>
      )}
      {activeSection === 'photos' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
            <ReHubStat icon={<ImageIcon size={18} />} value={userImages.length} label="Total images" tint="bg-slate-500/15 text-slate-200" />
            <ReHubStat icon={<Sparkles size={18} />} value={enhancedCount} label="Enhanced with AI" tint="bg-violet-500/15 text-violet-200" />
            <ReHubStat icon={<Clock size={18} />} value="30 days" label="Images are stored" tint="bg-emerald-500/15 text-emerald-200" />
            <ReHubStat icon={<Heart size={18} />} value={favourites.size} label="Favourites" tint="bg-rose-500/15 text-rose-200" />
          </div>
          <ReHubFilterBar
            filters={[
              { id: 'all', label: 'All Photos', count: userImages.length },
              { id: 'photoclear', label: 'Photoclear', count: userImages.filter((img) => img.service_type === 'photoclear').length },
              { id: 'staging', label: 'Virtual Staging', count: userImages.filter((img) => img.service_type === 'staging').length },
              { id: 'favourites', label: 'Favourites', count: favourites.size },
              { id: 'processing', label: 'Processing', count: userImages.filter((img) => img.status === 'processing').length },
              { id: 'failed', label: 'Failed', count: userImages.filter((img) => img.status === 'failed').length },
            ]}
            activeId={serviceFilter}
            onChange={(id) => { setServiceFilter(id as typeof serviceFilter); setGalleryPage(0); }}
            sort={sortOrder}
            onSort={(value) => { setSortOrder(value); setGalleryPage(0); }}
            range={dateRange}
            onRange={(value) => { setDateRange(value); setGalleryPage(0); }}
            view={galleryView}
            onView={setGalleryView}
          />
          {loadingImages ? (
            <ReHubLoadingSkeleton />
          ) : filteredImages.length === 0 ? (
            <ReHubEmptyState
              icon={<ImageIcon size={22} />}
              title="No photos in this view"
              body="Upload a property photo to declutter it, or change the filters."
              action={<button type="button" onClick={() => setUploadOpen(true)} className="rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white">Upload Photos</button>}
            />
          ) : (
            <ReHubGallery page={galleryPage} pageSize={IMAGES_PER_PAGE} total={filteredImages.length} onPageChange={setGalleryPage} view={galleryView}>
              {displayedImages.map((img) => {
                const enhanced = img.status === 'completed' && Boolean(img.output_url);
                const badge = img.status === 'processing' ? 'Processing' : img.status === 'failed' ? 'Failed' : enhanced ? 'AI Enhanced' : 'Original';
                const tone = img.status === 'failed' ? 'danger' : img.status === 'processing' ? 'warning' : enhanced ? 'accent' : 'neutral';
                const serviceLabel = img.service_type === 'staging' ? 'Virtual Staging' : 'Photoclear';
                return (
                  <ReHubImageCard
                    key={img.id}
                    layout={galleryView}
                    src={getThumbnailUrl(enhanced ? img.output_url : img.input_url, 640)}
                    alt={imageTitle(img)}
                    title={imageTitle(img)}
                    meta={`${formatCardDate(img.created_at)} · ${serviceLabel}`}
                    badge={badge}
                    badgeTone={tone}
                    selected={selectedIds.has(img.id)}
                    onSelect={(checked) => setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (checked) next.add(img.id); else next.delete(img.id);
                      return next;
                    })}
                    favourite={favourites.has(img.id)}
                    onToggleFavourite={() => toggleFavourite(img.id)}
                    busy={img.status === 'processing' || deletingId === img.id || editingId === img.id}
                    onOpen={() => { setLightboxShowOriginal(false); setLightboxImageLoaded(false); setLightboxImage(img); }}
                    menu={[
                      { id: 'view', label: 'View', onSelect: () => { setLightboxShowOriginal(false); setLightboxImage(img); } },
                      { id: 'download', label: 'Download', disabled: img.status !== 'completed' || !img.output_url, onSelect: () => handleDownload(img.output_url, img.id) },
                      { id: 'edit', label: 'Continue editing', disabled: img.status !== 'completed' || !img.output_url, onSelect: () => handleContinueEdit(img.output_url, img.id) },
                      { id: 'copy', label: copiedId === img.id ? 'Link copied' : 'Copy link', disabled: !(img.output_url || img.input_url), onSelect: () => handleCopyLink(img.output_url || img.input_url, img.id) },
                      { id: 'staging', label: 'Send to Virtual Staging', disabled: !img.output_url, onSelect: () => handleSendToVirtualStaging(img.output_url) },
                      { id: 'dusk', label: 'Send to HyperDusk', disabled: !img.output_url, onSelect: () => handleSendToHyperdusk(img.output_url) },
                      { id: 'favourite', label: favourites.has(img.id) ? 'Remove favourite' : 'Add to favourites', onSelect: () => toggleFavourite(img.id) },
                      { id: 'delete', label: 'Delete', destructive: true, onSelect: () => handleDelete(img.id) },
                    ]}
                  />
                );
              })}
            </ReHubGallery>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-500">Images are stored for 30 days.</p>
            {filteredImages.some((img) => img.status === 'completed' && img.output_url) && (
              <button type="button" onClick={handleBulkDownload} disabled={bulkDownloading} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-3 py-2 text-sm text-slate-200 hover:bg-white/5 disabled:opacity-50">
                {bulkDownloading ? <Loader2 size={14} className="animate-spin" /> : <Archive size={14} />}
                {bulkDownloading ? `Downloading ${bulkProgress}%` : 'Download all completed'}
              </button>
            )}
          </div>
          <ReHubBatchActions
            count={selectedIds.size}
            onClear={() => setSelectedIds(new Set())}
            actions={[
              { id: 'download', label: bulkDownloading ? `Downloading ${bulkProgress}%` : 'Download selected', onClick: downloadSelected, disabled: bulkDownloading || bulkDeleting },
              { id: 'favourite', label: 'Add to favourites', onClick: () => selectedIds.forEach((id) => { if (!favourites.has(id)) toggleFavourite(id); }) },
              { id: 'delete', label: 'Delete selected', destructive: true, disabled: bulkDeleting, onClick: () => deleteImages([...selectedIds]) },
            ]}
          />
        </div>
      )}
                {/* CREDITS SECTION - ENSURE VISIBILITY */}
                {activeSection === 'credits' && (
                    <div className="max-w-5xl mx-auto animate-fade-in space-y-8">
                        {/* Buy only what you need */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-slate-200 text-center">Buy only what you need</h3>
                            <div className="max-w-md mx-auto">
                                <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 flex flex-col">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Zap className="text-orange-400" size={22} />
                                        <span className="font-bold text-white">{EXACT_CREDITS.name}</span>
                                    </div>
                                    <p className="text-sm text-slate-400 mb-4">{EXACT_CREDITS.description}</p>
                                    <div className="flex items-center gap-3 mb-4">
                                        <input
                                            type="number"
                                            min={MIN_CUSTOM_CREDITS}
                                            max={MAX_CUSTOM_CREDITS}
                                            value={customCreditsAmount}
                                            onChange={(e) => {
                                                const v = parseInt(e.target.value, 10);
                                                if (!e.target.value.trim()) return;
                                                setCustomCreditsAmount(isNaN(v) ? MIN_CUSTOM_CREDITS : Math.min(MAX_CUSTOM_CREDITS, Math.max(MIN_CUSTOM_CREDITS, v)));
                                            }}
                                            onBlur={() => setCustomCreditsAmount((c) => (isNaN(c) || c < MIN_CUSTOM_CREDITS ? MIN_CUSTOM_CREDITS : Math.min(MAX_CUSTOM_CREDITS, c)))}
                                            className="w-24 bg-black border border-slate-600 rounded-lg px-3 py-2 text-white font-semibold text-center focus:outline-none focus:border-orange-500"
                                        />
                                        <span className="text-slate-400">credits</span>
                                    </div>
                                    <div className="text-slate-400 text-sm mb-6">
                                        {currency === 'GBP' ? '£' : '$'}{(currency === 'GBP' ? EXACT_CREDITS.prices.GBP.amountPerCredit : EXACT_CREDITS.prices.USD.amountPerCredit).toFixed(2)} per credit · Total: {currency === 'GBP' ? '£' : '$'}
                                        {(currency === 'GBP' ? customCreditsAmount * EXACT_CREDITS.prices.GBP.amountPerCredit : customCreditsAmount * EXACT_CREDITS.prices.USD.amountPerCredit).toFixed(2)}
                                    </div>
                                    <button
                                        onClick={() => {
                                            const priceId = currency === 'GBP' ? EXACT_CREDITS.prices.GBP.id : EXACT_CREDITS.prices.USD.id;
                                            handleCheckout(customCreditsAmount, priceId, 'payment', customCreditsAmount);
                                        }}
                                        disabled={!!processingCheckout}
                                        className="w-full py-3 rounded-xl font-semibold bg-orange-600 hover:bg-orange-500 text-white transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                    >
                                        {processingCheckout?.startsWith(currency === 'GBP' ? EXACT_CREDITS.prices.GBP.id : EXACT_CREDITS.prices.USD.id) ? (
                                            <Loader2 className="animate-spin" size={18} />
                                        ) : (
                                            `Buy ${customCreditsAmount} credits`
                                        )}
                                    </button>
                                </div>
                            </div>
                            <p className="text-center text-sm text-slate-500">Or get a better per-credit rate with a plan or top-up pack below.</p>
                        </div>

                        {/* Currency and Billing Toggle */}
                        <div className="flex flex-col items-center gap-4">
                            {/* Currency Switcher */}
                            <div className="bg-slate-900/50 p-1 rounded-xl border border-slate-800 flex items-center gap-1">
                                <button
                                    onClick={() => setCurrency('USD')}
                                    className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${currency === 'USD' ? 'bg-slate-700 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                >
                                    $ USD
                                </button>
                                <button
                                    onClick={() => setCurrency('GBP')}
                                    className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${currency === 'GBP' ? 'bg-slate-700 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                >
                                    £ GBP
                                </button>
                            </div>
                            
                            {/* Billing Toggle */}
                            <div className="bg-slate-900/50 p-1 rounded-xl border border-slate-800 flex items-center gap-1">
                                <button
                                    onClick={() => setBillingTab('subscription')}
                                    className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${billingTab === 'subscription' ? 'bg-orange-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                >
                                    Monthly Plans
                                </button>
                                <button
                                    onClick={() => setBillingTab('topup')}
                                    className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${billingTab === 'topup' ? 'bg-orange-600 text-white shadow-lg' : 'text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                >
                                    One-time Top Up
                                </button>
                            </div>
                        </div>

                        {/* Products Grid */}
                        {displayProducts.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {displayProducts.map((product) => (
                                    <div key={product.id} className={`relative bg-slate-900 border rounded-2xl p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${product.popular ? 'border-orange-500/50 shadow-orange-900/10' : 'border-slate-800 hover:border-slate-700'}`}>
                                        {product.popular && (
                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-orange-600 to-orange-400 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg tracking-wide uppercase">
                                                Most Popular
                                            </div>
                                        )}
                                        <div className="mb-4">
                                            <h3 className="text-lg font-bold text-white mb-1">{product.name}</h3>
                                            <div className="flex items-baseline gap-1">
                                                <span className="text-2xl font-bold text-slate-100">{product.currency === 'GBP' ? '£' : '$'}{product.displayAmount}</span>
                                                {billingTab === 'subscription' && <span className="text-xs text-slate-500">/mo</span>}
                                            </div>
                                        </div>

                                        <div className="flex-1 space-y-4 mb-8">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-orange-400 border border-slate-700">
                                                    <Zap size={20} className="fill-orange-400" />
                                                </div>
                                                <div>
                                                    <p className="font-bold text-slate-200 text-lg">{product.credits}</p>
                                                    <p className="text-xs text-slate-500 uppercase font-semibold">Credits</p>
                                                    {product.costPerCredit && (
                                                        <p className="text-[10px] text-slate-400 mt-0.5">
                                                            {product.costPerCredit}
                                                            {product.valueLabel && (
                                                                <span className="text-slate-400 ml-1">• {product.valueLabel}</span>
                                                            )}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <ul className="space-y-2">
                                                <li className="flex items-start gap-2 text-sm text-slate-400">
                                                    <Check size={16} className="text-green-500 shrink-0 mt-0.5" />
                                                    <span>High Speed Processing</span>
                                                </li>
                                                <li className="flex items-start gap-2 text-sm text-slate-400">
                                                    <Check size={16} className="text-green-500 shrink-0 mt-0.5" />
                                                    <span>Commercial Usage</span>
                                                </li>
                                                {billingTab === 'subscription' && product.thirdFeature && (
                                                    <li className="flex items-start gap-2 text-sm text-slate-400">
                                                    <Check size={16} className="text-green-500 shrink-0 mt-0.5" />
                                                    <span>{product.thirdFeature}</span>
                                                    </li>
                                                )}
                                            </ul>
                                        </div>

                                        <button
                                            onClick={() => handleCheckout(product.credits, product.priceId, billingTab === 'subscription' ? 'subscription' : 'payment')}
                                            disabled={processingCheckout === product.priceId}
                                            className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${product.popular ? 'bg-orange-600 hover:bg-orange-500 text-white shadow-lg shadow-orange-900/20' : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'}`}
                                        >
                                            {processingCheckout === product.priceId ? <Loader2 className="animate-spin" size={18} /> : (billingTab === 'subscription' ? 'Subscribe' : 'Buy Now')}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-12 border border-slate-800 rounded-xl bg-slate-900/50">
                                <div className="w-12 h-12 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-500">
                                    <ShoppingBag size={24} />
                                </div>
                                <h3 className="text-lg font-medium text-slate-300">No products available</h3>
                                <p className="text-sm text-slate-500 max-w-sm mx-auto mt-2">
                                    We couldn't load the product catalog. Please check your connection or try again later.
                                </p>
                            </div>
                        )}
                        
                        {/* Error Display */}
                        {checkoutError && (
                             <div className="bg-red-900/20 border border-red-900/50 rounded-xl p-4 flex items-start gap-3 text-red-400 text-sm max-w-2xl mx-auto animate-shake">
                                 <ShieldAlert className="shrink-0 mt-0.5" size={18} />
                                 <div className="flex-1">
                                    <span className="font-semibold block mb-1">Checkout Failed</span>
                                    <span className="block opacity-90">{checkoutError}</span>
                                 </div>
                             </div>
                        )}
                        
                        {/* Promo Code Section */}
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md mx-auto">
                            <h3 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                                <Sparkles size={16} className="text-orange-400" />
                                Have a promo code?
                            </h3>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={promoCode}
                                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                    placeholder="Enter code"
                                    className="flex-1 bg-black border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-orange-500 transition-colors uppercase tracking-wide"
                                    onKeyDown={(e) => e.key === 'Enter' && handleRedeemPromo()}
                                />
                                <button
                                    onClick={handleRedeemPromo}
                                    disabled={redeemingPromo || !promoCode.trim()}
                                    className="bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 disabled:text-slate-400 text-white font-semibold px-6 py-2.5 rounded-lg transition-all flex items-center gap-2 text-sm"
                                >
                                    {redeemingPromo ? <Loader2 size={16} className="animate-spin" /> : 'Redeem'}
                                </button>
                            </div>
                            {promoMessage && (
                                <div className={`mt-3 p-3 rounded-lg text-sm flex items-center gap-2 ${promoMessage.type === 'success' ? 'bg-green-900/30 border border-green-800 text-green-400' : 'bg-red-900/30 border border-red-800 text-red-400'}`}>
                                    {promoMessage.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                                    {promoMessage.text}
                                </div>
                            )}
                        </div>
                        
                        <div className="text-center text-xs text-slate-500 max-w-2xl mx-auto">
                             <p>Secure payment processing via Stripe. You can cancel subscriptions at any time from the settings menu.</p>
                        </div>
                    </div>
                )}

                {/* TRANSACTIONS */}
                {activeSection === 'transactions' && (
                  <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
                      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-lg">
                          <div className="p-6 border-b border-slate-800">
                              <div className="flex items-center justify-between mb-4">
                                  <h3 className="text-lg font-semibold text-white">Transaction History</h3>
                                  <span className="text-xs text-slate-500">Last 100 transactions</span>
                              </div>
                              
                              {/* Filter Tabs */}
                              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                                  <button
                                      onClick={() => setTransactionFilter('all')}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                          transactionFilter === 'all' 
                                              ? 'bg-orange-600 text-white shadow-lg' 
                                              : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                                      }`}
                                  >
                                      All
                                  </button>
                                  <button
                                      onClick={() => setTransactionFilter('purchase')}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                          transactionFilter === 'purchase' 
                                              ? 'bg-orange-600 text-white shadow-lg' 
                                              : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                                      }`}
                                  >
                                      Purchases
                                  </button>
                                  <button
                                      onClick={() => setTransactionFilter('usage')}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                          transactionFilter === 'usage' 
                                              ? 'bg-orange-600 text-white shadow-lg' 
                                              : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                                      }`}
                                  >
                                      Usage
                                  </button>
                                  <button
                                      onClick={() => setTransactionFilter('refund')}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                          transactionFilter === 'refund' 
                                              ? 'bg-orange-600 text-white shadow-lg' 
                                              : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                                      }`}
                                  >
                                      Refunds
                                  </button>
                                  <button
                                      onClick={() => setTransactionFilter('bonus')}
                                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                          transactionFilter === 'bonus' 
                                              ? 'bg-orange-600 text-white shadow-lg' 
                                              : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                                      }`}
                                  >
                                      Bonuses
                                  </button>
                              </div>
                          </div>
                          {loadingTransactions ? (
                              <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div>
                          ) : (() => {
                              const filteredTransactions = transactionFilter === 'all' 
                                  ? transactions 
                                  : transactions.filter(tx => tx.type === transactionFilter);
                              
                              return filteredTransactions.length > 0 ? (
                                  <div className="overflow-x-auto">
                                      <table className="w-full text-left border-collapse">
                                          <thead>
                                              <tr className="bg-black/50 border-b border-slate-800 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                                  <th className="p-4 whitespace-nowrap">Date</th>
                                                  <th className="p-4 w-full">Description</th>
                                                  <th className="p-4 text-right whitespace-nowrap">Amount</th>
                                              </tr>
                                          </thead>
                                          <tbody className="divide-y divide-slate-800 text-sm text-slate-300">
                                              {filteredTransactions.map(tx => (
                                                  <tr key={tx.id} className="hover:bg-slate-800/50 transition-colors">
                                                      <td className="p-4 text-slate-400 whitespace-nowrap">
                                                          {new Date(tx.created_at).toLocaleDateString()} <span className="text-xs opacity-50 ml-1">{new Date(tx.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                                      </td>
                                                      <td className="p-4 font-medium">{tx.description}</td>
                                                      <td className={`p-4 text-right font-bold whitespace-nowrap ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>{tx.amount > 0 ? '+' : ''}{tx.amount}</td>
                                                  </tr>
                                              ))}
                                          </tbody>
                                      </table>
                                  </div>
                              ) : (
                                  <div className="text-center py-16 px-6">
                                      <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-600"><Receipt size={32} /></div>
                                      <h4 className="text-slate-300 font-medium mb-1">No {transactionFilter === 'all' ? 'transactions' : transactionFilter + ' transactions'} yet</h4>
                                      <p className="text-sm text-slate-500">Your credit history will appear here once you start creating!</p>
                                  </div>
                              );
                          })()}
                      </div>
                  </div>
                )}

                {/* SETTINGS */}
                {activeSection === 'settings' && (
                    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                            <h3 className="text-lg font-semibold text-white mb-6">Account Details</h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Email Address</label>
                                    <div className="flex items-center gap-3 bg-black border border-slate-800 rounded-xl px-4 py-3 text-slate-200"><Mail size={18} className="text-slate-500" />{user.email}</div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-1">Current Plan</label>
                                    <div className="flex items-center justify-between bg-black border border-slate-800 rounded-xl px-4 py-3">
                                        <div className="flex items-center gap-3 text-slate-200">
                                            <Package size={18} className={user.subscription_plan ? (isPhotoclear ? 'text-orange-400' : 'text-purple-400') : 'text-slate-500'} />
                                            <span>{user.subscription_plan || 'Free Tier'}</span>
                                        </div>
                                        {user.subscription_plan && user.stripe_customer_id ? (
                                            <button 
                                                onClick={handleManageSubscription}
                                                disabled={loadingPortal}
                                                className={isPhotoclear ? 'text-xs bg-orange-600 hover:bg-orange-500 text-white px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50' : 'text-xs bg-purple-600 hover:bg-purple-500 text-white px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50'}
                                            >
                                                {loadingPortal ? 'Loading...' : 'Manage Subscription'}
                                            </button>
                                        ) : (
                                            <button onClick={() => setActiveSection('credits')} className="text-xs text-orange-400 hover:text-orange-300 font-medium">Upgrade</button>
                                        )}
                                    </div>
                                </div>
                                <div className="pt-4 border-t border-slate-800">
                                    <button onClick={onLogout} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600/10 hover:bg-red-600/20 text-red-400 hover:text-red-300 rounded-xl font-medium transition-colors border border-red-800/50">
                                        <LogOut size={18} /> Sign Out
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                             <h3 className="text-lg font-semibold text-white mb-4">Danger Zone</h3>
                             <p className="text-sm text-slate-400 mb-6">Once you delete your account, there is no going back. Please be certain.</p>
                             <button 
                                 onClick={() => setShowDeleteConfirm(true)}
                                 className="text-red-400 hover:text-red-300 hover:bg-red-900/10 px-4 py-2 rounded-lg text-sm font-medium transition-colors border border-red-900/30"
                             >
                                 Delete Account
                             </button>
                        </div>
                    </div>
                )}
    </ReHubDashboardShell>

      {lightboxImage && (
        <ReHubImageViewer
          open
          title={imageTitle(lightboxImage)}
          imageUrl={viewerImage}
          originalUrl={lightboxImage.input_url && lightboxImage.output_url ? getLightboxUrl(lightboxImage.input_url) : null}
          onClose={() => setLightboxImage(null)}
          hasPrev={lightboxIndex > 0}
          hasNext={lightboxIndex >= 0 && lightboxIndex < viewableImages.length - 1}
          onPrev={lightboxIndex > 0 ? () => { setLightboxImageLoaded(false); setLightboxShowOriginal(false); setLightboxImage(viewableImages[lightboxIndex - 1]); } : undefined}
          onNext={lightboxIndex >= 0 && lightboxIndex < viewableImages.length - 1 ? () => { setLightboxImageLoaded(false); setLightboxShowOriginal(false); setLightboxImage(viewableImages[lightboxIndex + 1]); } : undefined}
          downloading={downloadingId === lightboxImage.id}
          onDownload={() => {
            const url = lightboxImage.output_url || lightboxImage.input_url;
            if (url) handleDownload(url, lightboxImage.id);
          }}
          extra={lightboxImage.output_url ? (
            <>
              <button type="button" className="rounded-lg bg-white/10 px-3 py-1.5 text-sm" onClick={() => handleContinueEdit(lightboxImage.output_url, lightboxImage.id)}>Edit</button>
              <button type="button" className="hidden rounded-lg bg-white/10 px-3 py-1.5 text-sm sm:inline" onClick={() => handleSendToVirtualStaging(lightboxImage.output_url)}>Stage</button>
            </>
          ) : undefined}
          footer={(
            <div className="mx-auto flex max-w-3xl flex-col gap-3">
              {showOriginalMissing && <p className="text-center text-xs text-slate-400">The original file isn’t available for this photo, so comparison is hidden.</p>}
              {!lightboxShowOriginal && (
                <AIInfoPanel
                  classification={(lightboxImage.ai_classification as AIContentClassification) || APP_DEFAULT_CLASSIFICATION.photoclear}
                  operation={lightboxImage.ai_operation || APP_DEFAULT_OPERATION.photoclear}
                  createdAt={lightboxImage.ai_created_at || lightboxImage.created_at}
                  hasSource={Boolean(lightboxImage.input_url)}
                  app="photoclear"
                />
              )}
            </div>
          )}
        />
      )}

      <ReHubUploadPanel
        open={uploadOpen}
        title="Upload photos"
        description="PhotoClear edits one photo at a time. Pick an intensity, then continue in the editor. Credits are only used when you run the restoration."
        accept={ACCEPTED_IMAGE_TYPES.join(',')}
        multiple
        files={uploadFiles}
        onFiles={addUploadFiles}
        onRemove={(id) => setUploadFiles((prev) => prev.filter((item) => item.id !== id))}
        onClose={() => setUploadOpen(false)}
        onSubmit={startUpload}
        submitLabel="Open in editor"
      >
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-slate-200">Declutter intensity</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {DECLUTTER_INTENSITIES.map((intensity) => {
              const quote = quotePhotoclearOperation(`photoclear_declutter_${intensity.id}`);
              const selected = intensityId === intensity.id;
              return (
                <label key={intensity.id} className={`cursor-pointer rounded-xl border px-3 py-3 ${selected ? 'border-orange-400 bg-orange-500/10' : 'border-white/10 bg-white/[0.02]'}`}>
                  <input type="radio" name="declutter-intensity" className="sr-only" checked={selected} onChange={() => setIntensityId(intensity.id)} />
                  <span className="block text-sm font-semibold">{intensity.fanLabel}</span>
                  <span className="mt-1 block text-xs text-slate-400">{intensity.description}</span>
                  <span className="mt-2 block text-xs font-medium text-orange-200">{quote ? formatPhotoclearCreditLabel(quote.creditCost) : ''}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      </ReHubUploadPanel>
      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-md w-full animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <ShieldAlert className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Delete Account</h3>
                <p className="text-sm text-slate-400">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-slate-300 mb-6">
              Are you sure you want to permanently delete your account? All your data, images, and remaining credits will be lost forever.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deletingAccount}
                className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {deletingAccount ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</>
                ) : (
                  <><Trash2 className="w-4 h-4" /> Delete Forever</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
